import { apiClient, buildApiUrl, throwApiError } from './apiClient';
import type { SimulationInsert, SimulationSetInsert, SimulationSetUpdate } from '@urbanmetrix/sdk';

export type SimulationSetRunVersion = 'mixed' | 'java';

type SimulationSetListParams = {
  geodataId?: string;
};

export async function getSimulationSets(params: SimulationSetListParams) {
  const response = await apiClient.api['simulation-sets'].get({
    query: params,
  });
  if (response.error) {
    throwApiError(response.error.value, response.error.status, true);
  }
  return response.data;
}

export async function getSimulationSet(id: string) {
  const response = await apiClient.api['simulation-sets']({ simulationSetId: id }).get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status, true);
  }
  return response.data;
}

export async function addSimulationSet(simulationSet: SimulationSetInsert) {
  const response = await apiClient.api['simulation-sets'].post(simulationSet);
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function addSimulationSetWithSimulations(
  simulationSet: SimulationSetInsert & { simulations: SimulationInsert[]; shadowEnabled: boolean }
) {
  const simulationSetApi = apiClient.api['simulation-sets'] as unknown as {
    'with-simulations': {
      post: (
        payload: SimulationSetInsert & { simulations: SimulationInsert[]; shadowEnabled: boolean }
      ) => Promise<{ data: unknown; error: { value: unknown; status: number } | null }>;
    };
  };

  const response = await simulationSetApi['with-simulations'].post(simulationSet);
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function replaceSimulationSetWithSimulations(
  id: string,
  simulationSet: SimulationSetInsert & { simulations: SimulationInsert[]; shadowEnabled: boolean }
) {
  const endpoint = apiClient.api['simulation-sets']({ simulationSetId: id }) as unknown as {
    put: (
      payload: SimulationSetInsert & { simulations: SimulationInsert[]; shadowEnabled: boolean }
    ) => Promise<{ data: unknown; error: { value: unknown; status: number } | null }>;
  };
  const response = await endpoint.put(simulationSet);
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function updateSimulationSet(id: string, simulationSet: SimulationSetUpdate) {
  const response = await apiClient.api['simulation-sets']({ simulationSetId: id }).patch(simulationSet);
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function deleteSimulationSet(id: string) {
  const response = await apiClient.api['simulation-sets']({ simulationSetId: id }).delete();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function runSimulationSet(id: string, version?: SimulationSetRunVersion) {
  const response = await apiClient.api['simulation-sets']({ simulationSetId: id }).run.post(undefined, {
    query: version === 'mixed' ? { version } : undefined,
  });
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export function downloadSimulationSetYaml(id: string) {
  return buildApiUrl(`/api/simulation-sets/${id}/yaml`);
}
